import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

const AboutPage = () => {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="relative bg-gray-900 text-white">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1516426122078-c23e76319801?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80')` 
          }}
        ></div>
        <div className="container-custom mx-auto py-24 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">About Teem Wildlife Wonders</h1>
            <p className="text-xl text-gray-200 mb-8">
              Discover the story behind Kenya's premier tours and travel agency
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container-custom mx-auto py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* About Us Content */}
          <div>
            <h2 className="text-3xl font-bold mb-6 text-gray-800">Our Story</h2>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Teem Wildlife Wonders is a premier tours and travel agency based in Nairobi, Kenya. 
              Established in 2023, our mission is to provide unforgettable travel experiences that 
              showcase the diverse beauty, rich culture, and vibrant wildlife of Kenya.
            </p>
            <p className="text-gray-600 mb-6 leading-relaxed">
              We specialize in creating customized travel itineraries that cater for the unique 
              preferences and interests of our clients, ensuring a personalized and enriching adventure.
            </p>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Our team of experienced guides and travel experts are passionate about Kenya and its 
              natural wonders. We take pride in our deep knowledge of the local landscapes, wildlife, 
              and cultural heritage, allowing us to offer authentic and immersive experiences that go 
              beyond the typical tourist attractions.
            </p>
            <div className="mt-8">
              <Link 
                to="/contact" 
                className="bg-primary hover:bg-primary-dark text-white font-semibold py-3 px-6 rounded-md transition duration-300"
              >
                Contact Us
              </Link>
            </div>
          </div>
          
          {/* Image */}
          <div className="rounded-lg overflow-hidden shadow-xl">
            <img 
              src="https://images.unsplash.com/photo-1523805009345-7448845a9e53?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2072&q=80" 
              alt="Safari experience with Teem Wildlife Wonders" 
              className="w-full h-auto"
            />
          </div>
        </div>

        {/* Our Values */}
        <div className="mt-20">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-800">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="text-primary mb-4">
                <CheckCircleIcon className="h-12 w-12" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Sustainability</h3>
              <p className="text-gray-600">
                We are committed to responsible tourism practices that preserve Kenya's natural 
                environments and support local communities.
              </p>
            </div>
            
            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="text-primary mb-4">
                <CheckCircleIcon className="h-12 w-12" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Authenticity</h3>
              <p className="text-gray-600">
                We provide genuine experiences that connect travelers with the real Kenya, 
                its people, and its diverse cultures.
              </p>
            </div>
            
            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="text-primary mb-4">
                <CheckCircleIcon className="h-12 w-12" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Excellence</h3>
              <p className="text-gray-600">
                We strive for the highest standards in every aspect of our service, from 
                planning to execution.
              </p>
            </div>
          </div>
        </div>

        {/* Team Section */}
        <div className="mt-20">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-800">Our Team</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="mb-4 rounded-full overflow-hidden inline-block">
                <img 
                  src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=300&h=300&q=80" 
                  alt="David Kimani" 
                  className="w-48 h-48 object-cover"
                />
              </div>
              <h3 className="text-xl font-bold text-gray-800">David Kimani</h3>
              <p className="text-primary font-medium">Founder & CEO</p>
              <p className="text-gray-600 mt-2">
                With over 15 years of experience in the tourism industry, David founded Teem Wildlife Wonders 
                to share his passion for Kenya's natural beauty.
              </p>
            </div>
            
            <div className="text-center">
              <div className="mb-4 rounded-full overflow-hidden inline-block">
                <img 
                  src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=300&h=300&q=80" 
                  alt="Sarah Ochieng" 
                  className="w-48 h-48 object-cover"
                />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Sarah Ochieng</h3>
              <p className="text-primary font-medium">Head of Operations</p>
              <p className="text-gray-600 mt-2">
                Sarah ensures that every tour runs smoothly, coordinating our team of guides and logistics 
                to create seamless experiences.
              </p>
            </div>
            
            <div className="text-center">
              <div className="mb-4 rounded-full overflow-hidden inline-block">
                <img 
                  src="https://images.unsplash.com/photo-1531384441138-2736e62e0919?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=300&h=300&q=80" 
                  alt="Michael Mwangi" 
                  className="w-48 h-48 object-cover"
                />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Michael Mwangi</h3>
              <p className="text-primary font-medium">Lead Safari Guide</p>
              <p className="text-gray-600 mt-2">
                With extensive knowledge of Kenya's wildlife and landscapes, Michael leads our team of expert 
                guides to provide unforgettable safari experiences.
              </p>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-20 bg-gray-100 rounded-xl p-12 text-center">
          <h2 className="text-3xl font-bold mb-6 text-gray-800">Ready for an Unforgettable Adventure?</h2>
          <p className="text-gray-600 max-w-2xl mx-auto mb-8">
            Join us on a journey through Kenya's most breathtaking landscapes and encounter its magnificent wildlife. 
            Let us create a personalized travel experience that will leave you with memories to last a lifetime.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link 
              to="/tours" 
              className="bg-primary hover:bg-primary-dark text-white font-semibold py-3 px-6 rounded-md transition duration-300"
            >
              Explore Our Tours
            </Link>
            <Link 
              to="/contact" 
              className="bg-white hover:bg-gray-50 text-primary border border-primary font-semibold py-3 px-6 rounded-md transition duration-300"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
